import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { debounce } from "lodash";
import { Link as RouterLink } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import EmptyPage from "components/common/EmptyPage";
import performRequest from "network/perform-request";
import { leagueCustomerLineupsPlayersCsvRoute, leagueCustomerTournamentsRoute } from "network/api-routes";
import CsvDownloadButton, { csvFilename } from "components/common/CsvDownloadButton";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import GestionListPagination from "components/pages/Gestion/GestionListPagination";
import GestionPageHeader from "components/pages/Gestion/GestionPageHeader";
import GestionTable from "components/pages/Gestion/GestionTable";
import CreateTournamentButton from "components/pages/Gestion/CreateTournamentButton";
import { parseLeaguePaginatedResponse } from "components/pages/Gestion/gestion-pagination";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, index) => currentYear - index);

const tournamentId = (tournament) => tournament.uuid_ || tournament.revaId || tournament.id;

const isFinished = (tournament) => Boolean(tournament.winner_id);

const GestionTournaments = () => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [tournaments, setTournaments] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [year, setYear] = useState(String(currentYear));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const debouncedSearchRef = useRef(null);

  const load = useCallback(
    ({ name = "", pageIndex = 0, perPage = 25, status = "open", yearValue = String(currentYear), hard = false } = {}) => {
      if (!customerId || !user) return;

      if (hard) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }

      const payload = {
        page: pageIndex,
        page_size: perPage,
        name: name || undefined,
        status,
      };
      if (yearValue && yearValue !== "all") {
        payload.year = yearValue;
      }

      performRequest("GET", leagueCustomerTournamentsRoute(customerId), payload, true, user)
        .then((response) => {
          const data = parseLeaguePaginatedResponse(response.data);
          setTournaments(data.results || []);
          setPage(data.page ?? 0);
          setTotalPages(data.totalPages || 1);
          setTotalResults(data.totalResults || 0);
          setPageSize((current) => data.pageSize || perPage || current);
        })
        .catch((error) => {
          enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
            variant: "error",
          });
          setTournaments([]);
          setTotalResults(0);
        })
        .finally(() => {
          setInitialLoading(false);
          setRefreshing(false);
        });
    },
    [customerId, enqueueSnackbar, intl, user],
  );

  useEffect(() => {
    setNameFilter("");
    setStatusFilter("open");
    setYear(String(currentYear));
    setPage(0);
    setPageSize(25);
    setTournaments([]);
    setTotalResults(0);
    load({ hard: true, pageIndex: 0, perPage: 25, status: "open", yearValue: String(currentYear) });
  }, [customerId, load]);

  useEffect(() => {
    debouncedSearchRef.current = debounce((value, perPage, status, yearValue) => {
      setPage(0);
      load({ name: value, pageIndex: 0, perPage, status, yearValue });
    }, 500);

    return () => debouncedSearchRef.current?.cancel();
  }, [load]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setNameFilter(value);
    if (value === "") {
      debouncedSearchRef.current?.cancel();
      setPage(0);
      load({ name: "", pageIndex: 0, perPage: pageSize, status: statusFilter, yearValue: year });
      return;
    }
    debouncedSearchRef.current?.(value, pageSize, statusFilter, year);
  };

  const handleStatusChange = (nextStatus) => {
    if (nextStatus === statusFilter) return;
    setStatusFilter(nextStatus);
    setPage(0);
    load({ name: nameFilter, pageIndex: 0, perPage: pageSize, status: nextStatus, yearValue: year });
  };

  const handleYearChange = (event) => {
    const nextYear = event.target.value;
    setYear(nextYear);
    setPage(0);
    load({ name: nameFilter, pageIndex: 0, perPage: pageSize, status: statusFilter, yearValue: nextYear });
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    load({ name: nameFilter, pageIndex: nextPage, perPage: pageSize, status: statusFilter, yearValue: year });
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(0);
    load({ name: nameFilter, pageIndex: 0, perPage: nextPageSize, status: statusFilter, yearValue: year });
  };

  if (!customerId) return null;

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <GestionPageHeader
          title={intl.formatMessage({ id: "gestion.tournaments.title" }, { name: customerName || "" })}
          subtitle={intl.formatMessage({ id: "gestion.tournaments.subtitle" })}
          actions={
            <>
              <CreateTournamentButton
                customerId={customerId}
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<AddIcon />}
              >
                {intl.formatMessage({ id: "gestion.tournaments.create" })}
              </CreateTournamentButton>
              <Button
                variant={statusFilter === "open" ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => handleStatusChange("open")}
              >
                {intl.formatMessage({ id: "gestion.tournaments.open" })}
              </Button>
              <Button
                variant={statusFilter === "finished" ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => handleStatusChange("finished")}
              >
                {intl.formatMessage({ id: "gestion.tournaments.finished" })}
              </Button>
              <CsvDownloadButton
                labelId="league.csv.exportOrgLineups"
                url={leagueCustomerLineupsPlayersCsvRoute(customerId)}
                filename={csvFilename(customerName, "lineups")}
                user={user}
              />
            </>
          }
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap flexWrap="wrap">
          <TextField
            size="small"
            label={intl.formatMessage({ id: "Por nombre" })}
            value={nameFilter}
            onChange={handleSearchChange}
            sx={{ minWidth: 220, flex: 1 }}
          />
          <TextField
            select
            size="small"
            label={intl.formatMessage({ id: "gestion.tournaments.year" })}
            value={year}
            onChange={handleYearChange}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">{intl.formatMessage({ id: "gestion.tournaments.allYears" })}</MenuItem>
            {YEAR_OPTIONS.map((option) => (
              <MenuItem key={option} value={String(option)}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <GestionListPagination
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          totalResults={totalResults}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          disabled={initialLoading || refreshing}
        />

        {initialLoading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tournaments.length === 0 ? (
          <EmptyPage text={intl.formatMessage({ id: "gestion.tournaments.empty" })} />
        ) : (
          <Box sx={{ position: "relative", opacity: refreshing ? 0.6 : 1, transition: "opacity 0.2s" }}>
            {refreshing && (
              <CircularProgress size={24} sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }} />
            )}
            <GestionTable
              headers={[
                { key: "name", title: intl.formatMessage({ id: "gestion.tournaments.col.name" }) },
                { key: "year", title: intl.formatMessage({ id: "gestion.tournaments.col.year" }) },
                { key: "category", title: intl.formatMessage({ id: "gestion.tournaments.col.category" }) },
                {
                  key: "status",
                  title: intl.formatMessage({ id: "gestion.tournaments.col.status" }),
                  renderCell: (_value, tournament) => (
                    <Chip
                      size="small"
                      color={isFinished(tournament) ? "default" : "success"}
                      label={intl.formatMessage({
                        id: isFinished(tournament)
                          ? "gestion.tournaments.status.finished"
                          : "gestion.tournaments.status.open",
                      })}
                    />
                  ),
                },
                {
                  key: "actions",
                  title: intl.formatMessage({ id: "gestion.actions" }),
                  align: "right",
                  renderCell: (_value, tournament) => (
                    <Tooltip title={intl.formatMessage({ id: "gestion.tournaments.openTournament" })}>
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={GESTION_ROUTES.tournament(customerId, tournamentId(tournament))}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                },
              ]}
              rows={tournaments.map((tournament) => ({
                ...tournament,
                id: tournamentId(tournament),
                year: tournament.year || "—",
                category: tournament.category || "—",
              }))}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default GestionTournaments;
